import { useEffect, useMemo, useState } from "react";
import { Alert, Box, Button, IconButton, InputAdornment, MenuItem, Pagination, Stack, TextField, Tooltip } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import SafeMobileRecordList from "../../../components/common/SafeMobileRecordList";

export default function MobileMsdsList({ rows, columns, query, onSearch, selection, onSelection, onEdit, onDelete, busy, loading, error, onRetry, focusId }) {
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState("product_name");
  const [descending, setDescending] = useState(false);
  const filtered = useMemo(() => {
    const words = String(query || "").trim().toLocaleLowerCase().split(/\s+/).filter(Boolean);
    return rows.filter(row => {
      const text = [...columns.map(column => row[column.accessorKey]), ...(row.machines || []).map(machine => machine.custom_name || machine.machine_name)].filter(value => typeof value === "string" || typeof value === "number").join(" ").toLocaleLowerCase();
      return words.every(word => text.includes(word));
    }).sort((a, b) => String(a[sort] || "").localeCompare(String(b[sort] || ""), "ko", { numeric: true }) * (descending ? -1 : 1));
  }, [rows, query, columns, sort, descending]);
  useEffect(() => setPage(1), [query, rows, sort, descending]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / 10));
  const currentPage = Math.min(page, totalPages);
  const visible = filtered.slice((currentPage - 1) * 10, currentPage * 10);
  const mobileColumns = columns.map(column => ({ key: column.accessorKey, label: column.header,
    render: row => column.Cell ? column.Cell({ row: { original: row }, cell: { getValue: () => row[column.accessorKey] } }) : row[column.accessorKey] || "-",
  }));
  const selectedIds = visible.filter(row => selection[String(row.id)]).map(row => row.id);
  return <Stack spacing={1.25} className="safe-mobile-record" sx={{ pt: 1, "& .MuiTypography-root": { whiteSpace: "normal", maxWidth: "100%" } }}>
    <TextField fullWidth size="small" label="MSDS 검색" placeholder="제품명, 물질명, CAS No." value={query} onChange={event => onSearch(event.target.value)}
      InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
    <Stack direction="row" spacing={1} alignItems="center">
      <TextField select size="small" label="정렬 기준" value={sort} onChange={event => setSort(event.target.value)} sx={{ flex: 1, minWidth: 0 }}>
        {columns.filter(column => !["machines", "pdf_url", "guide_pdf_url"].includes(column.accessorKey)).map(column => <MenuItem key={column.accessorKey} value={column.accessorKey}>{column.header}</MenuItem>)}
      </TextField>
      <Tooltip title={descending ? "내림차순" : "오름차순"}><IconButton aria-label="정렬 방향 변경" aria-pressed={descending} onClick={() => setDescending(value => !value)}><SwapVertIcon /></IconButton></Tooltip>
      <Button color="error" variant="outlined" startIcon={<DeleteOutlineIcon />} disabled={busy || !rows.some(row => selection[String(row.id)])} onClick={onDelete}>선택 삭제</Button>
    </Stack>
    {error && <Alert severity="error" action={<Button color="inherit" onClick={onRetry}>재시도</Button>}>MSDS를 불러오지 못했습니다.</Alert>}
    <Box sx={{ "& article": { scrollMarginTop: 110 } }}>
      <SafeMobileRecordList rows={visible} columns={mobileColumns} titleKey="product_name" idField="id" selected={selectedIds}
        onSelect={ids => onSelection(previous => {
          const next = { ...previous };
          visible.forEach(row => { delete next[String(row.id)]; });
          ids.forEach(id => { next[String(id)] = true; });
          return next;
        })} onEdit={onEdit} emptyText={loading ? "불러오는 중..." : "검색 조건에 맞는 MSDS가 없습니다."}
        recordAttribute="data-msds-id" focusId={focusId} />
    </Box>
    <Pagination count={totalPages} page={currentPage} onChange={(_, value) => setPage(value)} size="small" siblingCount={0} sx={{ alignSelf: "center" }} />
  </Stack>;
}
